import { RADAR_MAX_SPOKES, RADAR_MIN_SPOKES } from '../constants'
import type { ProgressPeriod, ThisWeekRadarAxis } from '../types'
import { listChartStats, periodValue } from './progressChart'
import { getStatName } from './statNames'
import { formatStatValue, isDerivedStat } from './statValues'

export interface BuildThisWeekRadarArgs {
  thisWeek: ProgressPeriod
  /** The loaded range's periods (weeks or months) the axes are scaled against. */
  series: ProgressPeriod[]
  statNames: Record<string, string>
}

/** A period's value per game; null for a period without games. */
const perGame = (period: ProgressPeriod, abbr: string): number | null =>
  period.games_played > 0
    ? (periodValue(period, abbr) ?? 0) / period.games_played
    : null

/**
 * The week's per-game value against the best per-game period. Per game, so a
 * month bucket (1Y / All) compares fairly with a single week.
 */
const axisRatio = (
  thisWeek: ProgressPeriod,
  series: ProgressPeriod[],
  abbr: string
): number => {
  const week = perGame(thisWeek, abbr) ?? 0
  const best = Math.max(
    week,
    ...series.map((period) => perGame(period, abbr) ?? 0)
  )
  return best > 0 ? week / best : 0
}

/**
 * The spokes, from whatever the sport's API stats are: count stats only, in
 * API order (this week first, then the range). Games played is the per-game
 * denominator, and a rate (PTS/G, SV%) would repeat another spoke's shape.
 */
const pickRadarStats = (
  thisWeek: ProgressPeriod,
  series: ProgressPeriod[]
): string[] =>
  listChartStats([thisWeek, ...series])
    .filter((abbr) => !isDerivedStat(abbr))
    .slice(0, RADAR_MAX_SPOKES)

/** The This Week stats as radar axes, each scaled to the player's best. */
export const buildThisWeekRadar = ({
  thisWeek,
  series,
  statNames,
}: BuildThisWeekRadarArgs): ThisWeekRadarAxis[] =>
  pickRadarStats(thisWeek, series).map((abbr) => ({
    abbr,
    name: getStatName(statNames, abbr),
    value: formatStatValue(periodValue(thisWeek, abbr), abbr),
    ratio: axisRatio(thisWeek, series, abbr),
  }))

/** Enough spokes for the radar to have a shape. */
export const canDrawRadar = (axes: ThisWeekRadarAxis[]): boolean =>
  axes.length >= RADAR_MIN_SPOKES
